/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import { Metadata } from "./metadata.js";
import {
    ValidExportFormats as VEF,
    ExportType as ET,
    SketchLayerType as SLT,
    ConstExportOptions as CEO
} from "./constants.js";
import * as Constants from "./constants.js";
import { BUtils, unitedRect } from "./qtbridgeutils.js";
import { SStyle } from "./sstyle.js";
import { Document, Rectangle } from "sketch/dom";
import { SDocument } from "./sdocument.js";

export const LayerMetaDict = {
    qmlId: defaultQmlId,
    uuid: defaultUuid,
    exportType: defaultExportType,
    textDetails: defaultTextDetails,
    textIsRendered: undefined,
    assetData: undefined,
    typeUuid: defaultTypeUuid,
    typeName: defaultQmlType,
    extraImports: undefined,
    rPath: defaultRPath,
    clip: undefined,
    createAlias: undefined,
    qmlVisible: defaultQmlVisible,
    qmlProperties: undefined,
    opacity: defaultOpacity,
    transformation: defaultTransformation,
    annotation: undefined
};

export class SLayer {
    constructor(layerInstance) {
        this._instance = layerInstance;
        this._metadata = new Metadata(layerInstance, LayerMetaDict);
    }

    instance() {
        return this._instance;
    }

    canHaveAsset() {
        let type = this.layerType();
        if (type === SLT.Text && this.textIsRendered()) {
            return true;
        }
        return (
            type !== SLT.Text &&
            type !== SLT.Hotspot &&
            type !== SLT.SymbolInstance &&
            type !== SLT.Unknown
        );
    }

    haveAsset() {
        if (!this._instance || !this.canHaveAsset()) {
            return false;
        }
        let result = false;
        if (this.layerType() === SLT.Group) {
            // Walk the group and see if any child is merged.
            result = this._instance.layers.some(layerInstance => {
                let cLayer = new SLayer(layerInstance);
                let cet = cLayer.exportType();
                if (cet === ET.SkipExport || !cLayer.canHaveAsset()) {
                    return false;
                }
                if (cLayer.layerType() === SLT.Group) {
                    if (cet === ET.MergedToParent && cLayer.haveAsset()) {
                        return true;
                    }
                } else if (cet === ET.MergedToParent) {
                    return true;
                }
            });
        } else {
            result = true;
        }
        return result;
    }

    layerId() {
        let id;
        try {
            id = this._instance.id;
        } catch (e) {
            BUtils.logError(`Can not get layer property (id). ${e}`);
        }
        return id;
    }

    layerName() {
        let name;
        try {
            name = this._instance.name;
        } catch (e) {
            BUtils.logError(`Can not get layer property (name). ${e}`);
        }
        return name;
    }

    layerType() {
        let type;
        try {
            type = this._instance.type;
        } catch (e) {
            BUtils.logError(`Can not get layer property (type). ${e}`);
        }
        return type;
    }

    assetRect() {
        if (this.layerType() !== SLT.Group) {
            return layerRectWithEffects.call(this);
        }
        let rect = null;
        this.forEachChildLayer(cl => {
            let etype = cl.exportType();
            if (!cl.instance().hidden && etype !== ET.SkipExport && etype !== ET.AsChild) {
                rect = rect ? unitedRect(rect, cl.assetRect()) : cl.assetRect();
            }
        });
        return rect;
    }

    haveChildren() {
        return this._instance && this._instance.layers && this._instance.layers.length > 0;
    }

    parentIsSkipped() {
        let result = false;
        let parentInstance = this._instance.parent;
        while (parentInstance && parentInstance.type !== "Page") {
            let layer = new SLayer(parentInstance);
            if (layer.exportType() === ET.SkipExport) {
                result = true;
                break;
            }
            parentInstance = parentInstance.parent;
        }
        return result;
    }

    libraryInstance() {
        // Returns library associated with a Symbolinstance.
        // Expect a valid library document only for user libraries.
        if (this.layerType() !== SLT.SymbolInstance) {
            return false;
        }
        if (this._instance.master) {
            return this._instance.master.getLibrary();
        }
        return false;
    }

    exportOptions() {
        let options = [];
        this._instance.exportFormats.forEach(element => {
            let format = element.fileFormat;
            if (element.type === "ExportFormat") {
                let scaleValue = element.size === "2x" ? 2 : 1;
                options.push(Object.assign({}, { formats: format, scales: scaleValue }, CEO));
            }
        });
        if (options.length > 0) {
            return options;
        }
        return null;
    }

    setMetadata(name, value) {
        if (name == "textIsRendered") {
            if (value == false && this.exportType() == ET.MergedToParent) {
                // Text is not rendered as image. Reset the export type from merged to child.
                // Text can't be merged.
                this._metadata.setMetadata("exportType", ET.AsChild);
            }
        }
        return this._metadata.setMetadata(name, value);
    }

    resetMetadata(name) {
        return this._metadata.resetMetadata(name);
    }

    qmlId() {
        return this._metadata.metadata("qmlId");
    }

    uuid() {
        return this.layerId();
    }

    exportType() {
        return this._metadata.metadata("exportType");
    }

    typeName() {
        return this._metadata.metadata("typeName");
    }

    extraImports() {
        return this._metadata.metadata("extraImports");
    }

    clip() {
        return this._metadata.metadata("clip");
    }

    textIsRendered() {
        return this._metadata.metadata("textIsRendered");
    }

    createAlias() {
        return this._metadata.metadata("createAlias");
    }

    qmlVisible(value) {
        return this._metadata.metadata("qmlVisible");
    }

    qmlProperties() {
        return this._metadata.metadata("qmlProperties");
    }

    allMetadata() {
        return this._metadata.allMetadata();
    }

    sanitize() { }

    toJson() {
        if (this._instance) {
            return {
                name: this.layerName(),
                x: this._instance.frame.x,
                y: this._instance.frame.y,
                width: this._instance.frame.width,
                height: this._instance.frame.height,
                metadata: this.allMetadata()
            };
        } else {
            BUtils.logExportError("Invalid Layer instance.");
        }
        return {};
    }

    forEachChildLayer(callback, progressCallback) {
        if (!callback || !this._instance || typeof callback !== "function") {
            return;
        }

        if (!progressCallback || typeof progressCallback !== "function") {
            progressCallback = p => { };
        }

        if (this.haveChildren()) {
            let layerShare = 1 / this._instance.layers.length;
            let done = 0.0;
            this._instance.layers.forEach((childLayer, childIndex) => {
                callback(new SLayer(childLayer), childIndex);
                progressCallback((done += layerShare));
            });
        }
    }

    normalizedLayerInfo() {
        let info = this.allMetadata();
        info.layerName = this.layerName();
        info.layerType = this.layerType();
        info.parentIsSkipped = this.parentIsSkipped();
        return info;
    }
}

function defaultUuid(instance) {
    let l = new SLayer(instance);
    if (l.layerType() == SLT.SymbolMaster) {
        return instance.symbolId;
    }
    return l.layerId();
}

function defaultQmlId(instance) {
    let name = instance.name;
    // Remove all characters from the begning except a-z or A-Z
    var invalidStartReg = new RegExp("^[^A-Za-z]+");
    name = name.replace(invalidStartReg, "").trim();

    // Keep only Alphanumeric names.
    var filterReg = new RegExp("[^A-Za-z0-9]+", "g");
    name = name.replace(filterReg, "_").toLowerCase();
    if (name === "") {
        name = "item";
    }
    return name;
}

function defaultExportType(instance) {
    let l = new SLayer(instance);
    let t = l.layerType();
    if (t == SLT.Artboard || t == SLT.SymbolMaster) {
        return ET.AsComponent;
    } else if (t === SLT.Group || t === SLT.Text || t === SLT.SymbolInstance || t === SLT.Hotspot) {
        return ET.AsChild;
    }
    return ET.MergedToParent;
}

function defaultTextDetails(instance) {
    let l = new SLayer(instance);
    if (l.layerType() == SLT.Text && !l.textIsRendered()) {
        let style = new SStyle(instance.style);
        let textDetails = style.toTextDetails();
        textDetails["contents"] = instance.text;
        textDetails["multiline"] = instance.frame.height > textDetails.fontSize * 1.5;
        return textDetails;
    }
}

function defaultTypeUuid(instance) {
    let l = new SLayer(instance);
    if (l.layerType() === SLT.SymbolInstance) {
        return instance.symbolId;
    }
}

function defaultQmlType(instance) {
    let l = new SLayer(instance);
    if (l.layerType() === SLT.Hotspot) {
        return "MouseArea";
    }
}

function defaultQmlVisible(instance) {
    return true;
}

function defaultOpacity(instance) {
    let s = new SStyle(instance.style);
    return s.opacity();
}

function defaultTransformation(instance) {
    let l = new SLayer(instance);
    if (l.layerType() === SLT.Hotspot || (l.layerType() === SLT.Text && !l.textIsRendered())) {
        return instance.transform;
    }
}

function defaultRPath(instance) {
    const sDoc = new SDocument(Document.getSelectedDocument());
    if (!sDoc.organizeInPages())
        return undefined;

    // Organizing is enabled in settings.
    // Find page name for Components only.
    const l = new SLayer(instance);
    const lt = l.layerType();
    if (lt === SLT.Artboard || lt === SLT.SymbolMaster) {
        let parentInstance = instance.parent
        while (parentInstance && parentInstance.type != "Page") {
            parentInstance = parentInstance.parent
        }
        return parentInstance.type == "Page" ? parentInstance.name : null;
    }
}

function layerRectWithEffects() {
    // Calculate the rect of the layer including the effects. i.e shadow, glow, blurr etc.
    const ancestry = this._instance.sketchObject.ancestry();
    const SliceTrimmingClass = NSClassFromString("SketchRendering.MSSliceTrimming") ?? NSClassFromString("MSSliceTrimming")
    let layerRectInPage = SliceTrimmingClass.trimmedRectForLayerAncestry_(ancestry)
    let rectInPage = new Rectangle(layerRectInPage);
    if (this._instance.parent.type == "Page") {
        // Layers that resides in page's co-ordinate system. Just update asset's
        // position relative to its position. e.g. Artboards, Symbols masters
        BUtils.logExportInfo(`Artboard: ${this.layerName()}`);
        rectInPage.x = rectInPage.x - this._instance.frame.x;
        rectInPage.y = rectInPage.y - this._instance.frame.y;
        return rectInPage;
    }
    // Calculate rect is in page's co-ordinates. Change it to its parent's co-ordinate system.
    return rectInPage.changeBasis({ from: this._instance.getParentPage(), to: this._instance.parent });
}
